import { useParams } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Button,
  Grid,
  FormHelperText,
  Box,
} from "@mui/material";

import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm } from "react-hook-form";
import { useWorkLog } from "../../../context/WorkLogContext";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import clsx from "clsx";
import dayjs from "dayjs";

export const workLogSchema = z.object({
  content: z.string().min(1, "Task Name is required"),
  tracked_day: z.string().min(1, "Tracked Day is required"),
  tracked_time: z
    .number()
    .min(0, "Tracked Time is required")
    .max(24, "Tracked Time is over 24h"),
});

const WorkLogDialog = ({ isOpen, onClose, selectedEvent, selectedDate }) => {
  const { id: projectId } = useParams();
  const { addWorkLog, updateWorkLog } = useWorkLog();

  const {
    control,
    formState: { errors },
    setValue,
    reset,
    clearErrors,
    handleSubmit,
  } = useForm({
    resolver: zodResolver(workLogSchema),
    values: {
      content: selectedEvent?.title || "",
      tracked_day:
        selectedEvent?.extendedProps?.tracked_day ||
        selectedDate?.startStr ||
        "",
      tracked_time: selectedEvent?.extendedProps?.tracked_time || "",
    },
  });

  const handleSubmitWorkLog = (payload) => {
    const data = {
      project: projectId, // Ensure to include the event ID
      content: payload.content,
      tracked_day: payload.tracked_day,
      tracked_time: payload.tracked_time,
    };
    if (selectedEvent?.extendedProps?.workLogId) {
      data.workLogId = selectedEvent.extendedProps.workLogId;
    }

    selectedEvent ? updateWorkLog(data) : addWorkLog(data);
    onClose();
  };

  const handleCloseModal = () => {
    reset({
      content: '',
      tracked_day: '',
      tracked_time: ''
    });
    clearErrors();
    onClose()
  };

  const renderInput = (name, label, type = "text") => (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <TextField
          {...field}
          type={type}
          label={label}
          variant="outlined"
          onChange={(evt) => {
            const val = evt.target.value;
            setValue(name, name === "content" ? val : +val);
          }}
          fullWidth
          error={!!errors[name]}
          helperText={
            errors[name]
              ? errors[name].message === "Expected number, received string"
                ? "Tracked Time is required"
                : errors[name].message
              : ""
          }
        />
      )}
    />
  );

  return (
    <Dialog open={isOpen} onClose={handleCloseModal}>
      <DialogTitle>
        {selectedEvent ? "Edit Task" : "Add Task"}
      </DialogTitle>
      <DialogContent>
        <DialogContentText>
          {selectedEvent
            ? "Edit the details of the selected task."
            : "Enter the details for the new task."}
        </DialogContentText>
        <form onSubmit={handleSubmit(handleSubmitWorkLog)} noValidate>
          <Grid container spacing={2} mt={1}>
            <Grid item xs={12}>
              {renderInput("content", "Task Log")}
            </Grid>

            <Grid item xs={12} sm={6}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Controller
                  name="tracked_day"
                  control={control}
                  render={({ field }) => (
                    <DatePicker
                      {...field}
                      className={clsx("full-width", "date-picker", {
                        "Mui-error": !!errors.tracked_day,
                      })}
                      onChange={(val) => {
                        setValue("tracked_day", val.format("YYYY-MM-DD"));
                        clearErrors("tracked_day");
                      }}
                      value={
                        field.value ? dayjs(field.value, "YYYY-MM-DD") : null
                      }
                      label={"Tracked Day"}
                      renderInput={(params) => (
                        <TextField {...params} variant="outlined" />
                      )}
                    />
                  )}
                />
              </LocalizationProvider>
              {!!errors.tracked_day && (
                <FormHelperText sx={{ marginLeft: "14px" }} error>
                  {errors.tracked_day.message}
                </FormHelperText>
              )}
            </Grid>

            <Grid item xs={12} sm={6}>
              {renderInput("tracked_time", "Tracked Time", "number")}
            </Grid>

            {/* Submit Button */}
            <Grid item xs={12}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: "8px",
                }}
              >
                <Button onClick={handleCloseModal} color="secondary" variant="outlined">
                  Cancel
                </Button>
                <Button
                  onClick={handleSubmit}
                  type="submit"
                  color="primary"
                  variant="contained"
                >
                  {selectedEvent ? "Save" : "Add New Task"}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default WorkLogDialog;
